<script setup lang="ts">
  import { RouterLink, type RouteLocationRaw } from 'vue-router';

  interface BreadCrumb {
    text: string;
    url: RouteLocationRaw;
  }

  interface Props {
    crumbs: BreadCrumb[];
    showDashboard?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    showDashboard: false,
  });

  const emit = defineEmits<{
    beforeNavigate: [depth: number];
  }>();

  const handleCrumbClick = (index: number) => {
    // Calculate depth: how many levels back from the current (last) crumb
    const depth = props.crumbs.length - index;
    emit('beforeNavigate', depth);
  };

  const handleDashboardClick = () => {
    // Dashboard is always the deepest navigation (total crumbs + 1)
    const depth = props.crumbs.length + 1;
    emit('beforeNavigate', depth);
  };
</script>

<template>
  <div class="breadcrumbs-container flex items-center">
    <router-link
      v-if="props.showDashboard"
      class="crumb flex items-center dashboard-crumb"
      :to="{ name: 'dashboard' }"
      @click="handleDashboardClick"
    >
      Dashboard
    </router-link>

    <router-link
      class="crumb flex items-center"
      v-for="(prop, index) in props.crumbs"
      :to="prop.url"
      :key="prop.text"
      @click="handleCrumbClick(index)"
    >
      {{ prop.text }}
      <iconify-icon icon="mdi:slash-forward" v-if="index < props.crumbs.length - 1" />
    </router-link>
  </div>
</template>

<style scoped lang="scss">
  @import '@/assets/styles/variables.scss';

  .crumb {
    font-weight: 500;
    color: $primary-main;

    &:last-of-type {
      color: $neutral-gray;
      pointer-events: none;
    }

    &.dashboard-crumb {
      color: $primary-main;
    }
  }

  .crumb {
    svg {
      color: $input-placeholder;
    }
  }
</style>
